/**
 * Die Verhandlung — der Screen zu einer laufenden Sitzung.
 *
 * Teilnehmer mit Status, Live-Ticker der Buchungen, Ankündigen und Absagen,
 * das Pendel der Schnapsuhr. Alles Live-Verhalten kommt über den SSE-Strom;
 * jede Strom-Meldung stößt ein Nachladen des Standes an, damit Anzeige und
 * Datenbank nicht auseinanderlaufen können.
 */
import { useCallback, useState } from 'react'
import { useParams } from 'react-router-dom'
import { api, ApiFehler } from '../api/client.js'
import { formatDatumZeit, formatEinheiten, formatZeit } from '../api/format.js'
import { useLaden } from '../api/laden.js'
import { useSitzung } from '../api/sitzung.js'
import { useEventStrom } from '../api/strom.js'
import type { EventDetail as EventDetailDaten, TickerMeldung, TeilnehmerStatus } from '../api/typen.js'
import {
  Abschnitt,
  Amtsknopf,
  eingabeKlasse,
  Fehlermeldung,
  Laufvermerk,
  LeererZustand,
  Stempel,
  Urkunde,
} from '../bausteine/Bausteine.js'
import { Schnapsbattle } from '../bausteine/Battle.js'
import { KalenderKnopf } from '../bausteine/Kalender.js'
import {
  SchnapsuhrAnzeige,
  VollstreckungsOverlay,
  type AlarmSchlag,
} from '../bausteine/Schnapsuhr.js'
import { ZufallsAktionen } from '../bausteine/Zufall.js'

const STATUS_WORT: Record<TeilnehmerStatus, string> = {
  da: 'da',
  gegangen: 'gegangen',
  angekuendigt: 'angekündigt',
  abgesagt: 'abgesagt',
  vermisst: 'vermisst',
}

export function EventDetail() {
  const { id } = useParams()
  const eventId = Number(id)
  const { mitglied } = useSitzung()

  const detail = useLaden(() => api.get<EventDetailDaten>(`/api/events/${eventId}`), [eventId])
  /**
   * Offene Schläge. Holt der Server nach einer Pause mehrere Runden nach,
   * sollen daraus nicht mehrere Overlays hintereinander werden — die Kammer
   * fasst zusammen und verlangt eine Bestätigung.
   */
  const [schlaege, setSchlaege] = useState<AlarmSchlag[]>([])
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const [ansageMin, setAnsageMin] = useState('30')

  const beiMeldung = useCallback(
    (art: string, meldung: TickerMeldung) => {
      if (art === 'bereit') return
      if (art === 'schnapsuhr_alarm') {
        const d = meldung.daten ?? {}
        const schlag: AlarmSchlag = {
          rundeNr: d.rundeNr as number | undefined,
          einheiten: d.einheiten as number | undefined,
          betroffene: (d.betroffene as number[] | undefined) ?? [],
        }
        // Derselbe Schlag kann über Strom und Neuverbindung zweimal kommen.
        setSchlaege((offen) =>
          offen.some((s) => s.rundeNr === schlag.rundeNr) ? offen : [...offen, schlag],
        )
      }
      // Der Stand kommt immer frisch vom Server — die Meldung ist nur der Anstoß.
      detail.neuLaden()
    },
    [detail.neuLaden],
  )
  useEventStrom(Number.isFinite(eventId) ? eventId : null, beiMeldung)

  if (detail.laedt && !detail.daten) return <Laufvermerk />
  if (detail.fehler) return <Fehlermeldung text={detail.fehler} />
  const event = detail.daten
  if (!event) return null

  const ich = event.teilnehmer.find((t) => t.memberId === mitglied?.id)
  const abgeschlossen = event.status === 'abgeschlossen'

  async function aktion(pfad: string, body: unknown): Promise<void> {
    setLaeuft(true)
    setFehler(null)
    try {
      await api.post(`/api/events/${eventId}/${pfad}`, body)
      detail.neuLaden()
    } catch (f) {
      setFehler(f instanceof ApiFehler ? f.message : 'Der Antrag konnte nicht zugestellt werden.')
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <>
      <VollstreckungsOverlay
        schlaege={schlaege}
        namen={new Map(event.teilnehmer.map((t) => [t.memberId, t.name]))}
        eigeneMemberId={mitglied?.id}
        onSchliessen={() => setSchlaege([])}
      />

      <section className="pt-8">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-2xl leading-tight">{event.title}</h1>
            <p className="protokoll mt-1.5 normal-case">
              {formatDatumZeit(event.startsAt)} · {event.location}
            </p>
          </div>
          <Stempel
            status={abgeschlossen ? 'vollstreckt' : event.status === 'laufend' ? 'offen' : 'anhaengig'}
          />
        </div>
        {event.type === 'sonderschnapsevent' && (
          <p className="mt-2 text-xs text-siegel">
            Sonderschnapsevent — erste Einheit ab 15 min, Gesamtstrafe verdoppelt (§4).
          </p>
        )}
        <div className="mt-2">
          <KalenderKnopf eventId={event.id} titel={event.title} />
        </div>
      </section>

      {!abgeschlossen && (
        <div className="mt-6">
          <SchnapsuhrAnzeige
            stand={event.schnapsuhr}
            umschalten={(an) => void aktion('schnapsuhr', { an })}
          />
        </div>
      )}

      {/* Eigene Anträge: erscheinen, ankündigen, absagen. */}
      {!abgeschlossen && (
        <Abschnitt titel="Eigene Anträge">
          <div className="flex flex-col gap-2.5">
            {ich?.status !== 'da' && ich?.status !== 'gegangen' && (
              <Amtsknopf onClick={() => void aktion('check-in', {})} disabled={laeuft}>
                Ich bin da
              </Amtsknopf>
            )}
            {ich?.status !== 'da' && ich?.status !== 'gegangen' && (
              <div className="flex items-stretch gap-2">
                <input
                  type="number"
                  min={0}
                  max={1440}
                  value={ansageMin}
                  onChange={(e) => setAnsageMin(e.target.value)}
                  className={`${eingabeKlasse} w-24 shrink-0 text-center tabular-nums`}
                  aria-label="Angesagte Verspätung in Minuten"
                />
                <button
                  type="button"
                  disabled={laeuft}
                  onClick={() =>
                    void aktion('ankuendigung', {
                      art: 'verspaetung',
                      angesagteVerspaetungMin: Number(ansageMin),
                    })
                  }
                  className="flex-1 border border-messing/50 px-3 py-2.5 font-sans text-sm font-semibold tracking-wide text-messing uppercase transition-colors hover:border-messing hover:text-lampe disabled:border-eiche disabled:text-akte/40"
                >
                  min Verspätung ankündigen
                </button>
              </div>
            )}
            {ich?.status !== 'da' && ich?.status !== 'abgesagt' && (
              <Amtsknopf
                variante="leder"
                onClick={() => void aktion('ankuendigung', { art: 'absage' })}
                disabled={laeuft}
              >
                Absagen
              </Amtsknopf>
            )}
            {ich?.status === 'da' && (
              <>
                <p className="border border-kanzel/50 bg-kanzel/10 px-3 py-2.5 text-sm text-akte">
                  Erschienen um {ich.arrivedAtMs ? formatZeit(ich.arrivedAtMs) : '—'}
                  {ich.verspaetungMin !== null && ich.verspaetungMin > 0
                    ? ` — ${ich.verspaetungMin} min nach Aufruf.`
                    : ' — pünktlich. Die Kammer nimmt es zur Kenntnis.'}
                </p>
                <Amtsknopf variante="leder" onClick={() => void aktion('check-out', undefined)} disabled={laeuft}>
                  Ich gehe
                </Amtsknopf>
                <p className="protokoll normal-case">
                  Wer gegangen ist, wird von Schnapsuhr und Battles nicht mehr getroffen. Verlässt
                  die letzte Person die Sitzung, schließt die Kammer sie von selbst.
                </p>
              </>
            )}
            {ich?.status === 'gegangen' && (
              <Amtsknopf variante="leder" onClick={() => void aktion('check-in', {})} disabled={laeuft}>
                Ich bin wieder da
              </Amtsknopf>
            )}
          </div>
          <Fehlermeldung text={fehler} />
        </Abschnitt>
      )}

      <Abschnitt titel="Teilnehmer">
        <ul className="flex flex-col gap-1.5">
          {event.teilnehmer.map((t) => (
            <li
              key={t.memberId}
              className="flex items-center justify-between gap-3 border border-eiche bg-bank px-3.5 py-2.5"
            >
              <span className="flex min-w-0 items-center gap-2.5">
                <span aria-hidden="true">{t.avatarEmoji}</span>
                <span className="truncate text-lampe">{t.name}</span>
              </span>
              <span className="protokoll shrink-0 normal-case">
                {t.status === 'da' && t.verspaetungMin !== null && t.verspaetungMin > 0
                  ? `da · +${t.verspaetungMin} min`
                  : t.status === 'angekuendigt' && t.angesagteVerspaetungMin !== null
                    ? `angekündigt · +${t.angesagteVerspaetungMin} min`
                    : STATUS_WORT[t.status]}
              </span>
            </li>
          ))}
        </ul>
      </Abschnitt>

      {!abgeschlossen && (
        <Abschnitt titel="§11 · Schnapsbattle">
          <Schnapsbattle
            eventId={eventId}
            battles={event.battles}
            teilnehmer={event.teilnehmer}
            ticker={event.ticker}
            eigeneMemberId={mitglied?.id}
            neuLaden={detail.neuLaden}
          />
        </Abschnitt>
      )}

      <Abschnitt titel="Live-Ticker der Buchungen">
        {event.ticker.length === 0 ? (
          <LeererZustand text="Noch keine Buchungen. Die Kammer beobachtet." />
        ) : (
          <div className="flex flex-col gap-2.5">
            {[...event.ticker].reverse().map((posten) => {
              const wer = event.teilnehmer.find((t) => t.memberId === posten.memberId)
              return (
                <Urkunde key={posten.id} randnummer={posten.id}>
                  <div className="flex items-start justify-between gap-3">
                    <p className="text-sm text-lampe">
                      {wer ? `${wer.avatarEmoji} ${wer.name}` : `Mitglied ${posten.memberId}`} ·{' '}
                      {posten.paragraph} · {formatEinheiten(posten.amountUnits)}
                    </p>
                    <Stempel status={posten.status} />
                  </div>
                  <p className="mt-1 text-xs leading-relaxed text-akte/75">{posten.reasonText}</p>
                  <p className="protokoll mt-1.5 normal-case">{formatZeit(posten.createdAt)}</p>
                  {/* Die Zufallsfeatures greifen an offenen Posten. */}
                  {posten.status === 'offen' && !abgeschlossen && (
                    <ZufallsAktionen
                      posten={posten}
                      teilnehmer={event.teilnehmer}
                      eigeneMemberId={mitglied?.id}
                      neuLaden={detail.neuLaden}
                    />
                  )}
                </Urkunde>
              )
            })}
          </div>
        )}
      </Abschnitt>

    </>
  )
}
